import { useCallback } from 'react'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const GetPendingInvitations = graphql(/* GraphQL */ `
  query GetPendingInvitations($input: PendingInvitationsInput!) {
    pendingInvitations(input: $input) {
      id
      name
      email
      role
      collaborationRole
      invitedAt
      expiresAt
      inviteLink
      channelPermissions {
        channel {
          id
        }
      }
    }
  }
`)

export interface PendingInvitation {
  id: string
  name: string
  email: string
  role: 'admin' | 'member'
  collaborationRole: ReturnType<typeof graphql.scalar<'CollaborationRole'>>
  invitedAt: string
  expiresAt: string
  inviteLink: string
  /** The channels the invitee gets on accept; every org channel for Admins. */
  channelPermissions: Array<{ channel: { id: string } }>
}

interface UsePendingInvitationsReturn {
  pendingInvitations: PendingInvitation[]
  loading: boolean
  error: boolean
  refetch: () => void
}

/**
 * Fetches pending invitations for an organization from the dedicated
 * `pendingInvitations` query (pending invites are no longer bundled into
 * the `teamMembers` connection — the backend split them out in
 * TS-2026-03).
 *
 * `enabled` lets callers skip the query entirely, e.g. for viewers who
 * can't manage team members and so never see pending invitations.
 */
export const usePendingInvitations = ({
  organizationId,
  enabled = true,
}: {
  organizationId: string
  enabled?: boolean
}): UsePendingInvitationsReturn => {
  const { data, loading, error, refetch } = useQuery(GetPendingInvitations, {
    variables: { input: { organizationId } },
    skip: !organizationId || !enabled,
    fetchPolicy: 'network-only',
  })

  const handleRefetch = useCallback((): void => {
    void refetch()
  }, [refetch])

  // The `InvitationId` scalar is codegen'd as `unknown` (branded); coerce
  // to a plain string for the view-model so consumers don't have to.
  const pendingInvitations: PendingInvitation[] = (
    data?.pendingInvitations ?? []
  ).map((invitation) => ({
    id: String(invitation.id),
    name: invitation.name,
    email: invitation.email,
    role: invitation.role,
    collaborationRole: invitation.collaborationRole,
    invitedAt: invitation.invitedAt,
    expiresAt: invitation.expiresAt,
    inviteLink: invitation.inviteLink,
    channelPermissions: invitation.channelPermissions,
  }))

  return {
    pendingInvitations,
    loading,
    error: !!error,
    refetch: handleRefetch,
  }
}
